<template>
  <v-container
    class="pa-1 pa-sm-2 fill-height align-start"
    fluid
  >
    <v-row
      :class="{'flex-column': $vuetify.breakpoint.smAndDown}"
      class=""
      no-gutters
    >
      <v-col class="shrink pr-2">
        <v-card flat>
          <v-toolbar
            ref="deliveryStaffToolbar"
            :color="isDarkMode ? 'grey darken-4' : 'grey lighten-5'"
            flat
          >
            <v-toolbar-title class="grey--text text--darken-1 pr-2">
              {{ $tc('Delivery.Title', 0) }}
            </v-toolbar-title>

            <v-spacer />

            <v-btn
              v-if="userCanAdd"
              :color="$t('Delivery.Button.DeliveryStaff.Add.Color')"
              :icon="$vuetify.breakpoint.xsOnly"
              class="ma-0 px-2"
              text
              @click="showDeliveryStaffAddDialog"
            >
              <v-icon :left="$vuetify.breakpoint.smAndUp">
                {{ $t('Delivery.Button.DeliveryStaff.Add.Icon') }}
              </v-icon>&nbsp;

              <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Delivery.Button.DeliveryStaff.Add.Title') }}</span>
            </v-btn>
          </v-toolbar>

          <v-divider />

          <v-card-text class="pa-0 mt-1">
            <div
              ref="deliveryStaffList"
              style="overflow-y: auto;"
            >
              <div
                v-if="deliveryStaff.length <= 0"
                class="fill-height"
                style="position: relative;"
              >
                <div
                  class="text-center grey--text pa-6"
                  style="position: absolute; width: 100%; top: 50%; transform: translateY(-50%);"
                >
                  <v-icon
                    class="mb-4"
                    color="grey lighten-2"
                    size="128"
                  >
                    {{ $t('Delivery.ListView.NoData.Icon') }}
                  </v-icon>
                  <div
                    class="text-h6 mb-2"
                    style="height: auto;"
                  >
                    {{ $t('Delivery.ListView.NoData.Title') }}
                  </div>
                  <div
                    class="text-subtitle-1"
                    style="height: auto;"
                  >
                    {{ $t('Delivery.ListView.NoData.Subtitle') }}
                  </div>
                </div>
              </div>

              <template v-if="$vuetify.breakpoint.mdAndUp">
                <v-list
                  class="ma-0 pa-0"
                  three-line
                >
                  <template v-for="staff in deliveryStaff">
                    <v-list-item
                      :key="`staff-list-item-${staff.Id}`"
                      :class="[selectedDeliveryStaff && parseInt(staff.Id) === parseInt(selectedDeliveryStaff.Id) ? (isDarkMode ? 'green darken-4' : 'green lighten-4') : (isDarkMode ? 'grey darken-4' : 'grey lighten-4')]"
                      ripple
                      @click="deliveryStaffToggle(staff)"
                    >
                      <div
                        :class="staff?.User?.EftPos ? 'purple' : staff.HasActiveShift ? 'light-green' : 'grey lighten-1'"
                        class="ma-0 pa-0"
                        style="width: 5px; height: 100%; position: absolute; left: 0"
                      />

                      <v-avatar>
                        <v-icon
                          :color="staff?.User?.EftPos ? 'purple' : staff.HasActiveShift ? 'light-green' : 'grey lighten-1'"
                          x-large
                        >
                          account_circle
                        </v-icon>
                      </v-avatar>

                      <v-list-item-content class="pl-3">
                        <v-list-item-title class="">
                          {{ staff.Firstname + ' ' + staff.Lastname }}
                        </v-list-item-title>

                        <v-list-item-subtitle v-if="staff.Phone && !staff?.User?.EftPos">
                          {{ $t('Delivery.Common.InfoLabels.Telephone') }} {{ staff.Phone }}&nbsp;
                        </v-list-item-subtitle>

                        <v-list-item-subtitle
                          v-if="staff.HasActiveShift && !staff?.User?.EftPos"
                          class="text-caption"
                        >
                          {{ $t('Delivery.Common.ShiftsOrders') }} {{ staff.ShiftOrders.length }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action-text class="ml-2">
                        <v-btn
                          v-if="userCanAdd"
                          icon
                          ripple
                          small
                          @click.native.stop="showDeliveryStaffEditDialog(staff)"
                        >
                          <v-icon
                            :color="$t('Delivery.Button.DeliveryStaff.Edit.Color')"
                          >
                            {{ staff?.User?.EftPos ? 'mdi-eye' : $t('Delivery.Button.DeliveryStaff.Edit.Icon') }}
                          </v-icon>
                        </v-btn>

                        <v-btn
                          v-if="!staff.HasActiveShift && userCanDelete && !staff?.User?.EftPos"
                          class="red--text"
                          icon
                          ripple
                          small
                          @click.native.stop="showDeleteDeliveryStaffDialog(staff)"
                        >
                          <v-icon>close</v-icon>
                        </v-btn>
                      </v-list-item-action-text>
                    </v-list-item>

                    <v-divider
                      :key="`staff-list-divider-${staff.Id}`"
                      class="mb-1"
                    />
                  </template>
                </v-list>
              </template>

              <v-menu
                v-else
                bottom
                max-height="400"
                transition="slide-y-transition"
              >
                <template #activator="{on}">
                  <v-btn
                    block
                    color="primary ma-0"
                    dark
                    v-on="on"
                  >
                    {{ $t('Delivery.ListView.SelectDeliveryStaff.Title') }}
                  </v-btn>
                </template>

                <v-list
                  class="mb-1 mt-0 pb-0 pt-0"
                  three-line
                >
                  <template v-for="staff in deliveryStaff">
                    <v-list-item
                      :key="`list-item-${staff.Id}`"
                      :class="[selectedDeliveryStaff && parseInt(staff.Id) === parseInt(selectedDeliveryStaff.Id) ? (isDarkMode ? 'green darken-4' : 'green lighten-4') : (isDarkMode ? 'grey darken-4' : 'grey lighten-4')]"
                      ripple
                      @click="deliveryStaffToggle(staff)"
                    >
                      <div
                        :class="staff?.User?.EftPos ? 'purple' : staff.HasActiveShift ? 'light-green' : 'grey lighten-1'"
                        class="ma-0 pa-0"
                        style="width: 5px; height: 100%; position: absolute; left: 0"
                      />

                      <v-avatar>
                        <v-icon
                          :color="staff?.User?.EftPos ? 'purple' : staff.HasActiveShift ? 'light-green' : 'grey lighten-1'"
                          x-large
                        >
                          {{ $t('Delivery.Common.InfoLabels.Icon') }}
                        </v-icon>
                      </v-avatar>

                      <v-list-item-content class="pl-3">
                        <v-list-item-title>
                          {{ staff.Firstname + ' ' + staff.Lastname }}
                        </v-list-item-title>

                        <v-list-item-subtitle v-if="staff.Phone && !staff?.User?.EftPos">
                          {{ $t('Delivery.Common.InfoLabels.Telephone') }} {{ staff.Phone }}&nbsp;
                        </v-list-item-subtitle>

                        <v-list-item-subtitle
                          v-if="staff.HasActiveShift && !staff?.User?.EftPos"
                          class="text-caption"
                        >
                          {{ $t('Delivery.Common.ShiftsOrders') }} {{ staff.ShiftOrders.length }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action-text>
                        <v-btn
                          v-if="userCanManage"
                          icon
                          ripple
                          small
                          @click.native.stop="showDeliveryStaffEditDialog(staff)"
                        >
                          <v-icon color="grey lighten-0">
                            edit
                          </v-icon>
                        </v-btn>
                        <v-btn
                          v-if="!staff.HasActiveShift && userCanDelete && !staff?.User?.EftPos"
                          class="red--text"
                          icon
                          ripple
                          small
                          @click.native.stop="showDeleteDeliveryStaffDialog(staff)"
                        >
                          <v-icon>close</v-icon>
                        </v-btn>
                      </v-list-item-action-text>
                    </v-list-item>

                    <v-divider
                      :key="`list-item-divider-${staff.Id}`"
                      class="mb-1"
                    />
                  </template>
                </v-list>
              </v-menu>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="selectedDeliveryStaff">
        <v-divider
          v-if="$vuetify.breakpoint.smAndDown"
          class="mt-1"
        />

        <v-card
          v-if="selectedDeliveryStaff"
          :class="{'mt-2 pa-1': $vuetify.breakpoint.smAndDown}"
          flat
          style="margin-top: 2px;"
        >
          <v-toolbar
            ref="deliveryStaffToolbar"
            class="datatable-object"
            flat
          >
            <div
              :class="[selectedDeliveryStaff.HasActiveShift ? 'light-green' : 'grey lighten-1']"
              class="ma-0 pa-0"
              style="width: 5px; height: 100%; position: absolute; left: 0"
            />

            <v-avatar>
              <v-icon
                :class="[selectedDeliveryStaff.HasActiveShift ? 'light-green--text' : 'grey--text text--lighten-1']"
                x-large
              >
                {{ $t('Delivery.Common.InfoLabels.Icon') }}
              </v-icon>
            </v-avatar>

            <v-toolbar-title class="grey--text text--darken-1 ma-1 text-body-2">
              {{ selectedDeliveryStaff.Firstname + ' ' + selectedDeliveryStaff.Lastname }}
              <div class="grey--text text-caption">
                <span class="grey--text text--darken-1">
                  {{ $t('Delivery.Common.InfoLabels.Telephone') }}
                </span> {{ selectedDeliveryStaff.Phone }}

                <span
                  v-if="selectedDeliveryStaff.Email"
                  class="pl-2"
                >
                  <span class="grey--text text--darken-1">
                    {{ $t('Delivery.Common.InfoLabels.Email') }}
                  </span> {{ selectedDeliveryStaff.Email }}
                </span>
              </div>
            </v-toolbar-title>

            <v-spacer />

            <v-menu
              v-if="!selectedDeliveryStaff.HasActiveShift && userCanManage"
              v-model="startShiftPopOverVisible"
              :close-on-content-click="false"
              :nudge-width="85"
              :offset-y="true"
              bottom
              class="ma-0 pa-0"
            >
              <template #activator="{on}">
                <v-btn
                  :color="$t('Delivery.Button.Shift.Open.Color')"
                  :icon="$vuetify.breakpoint.xsOnly"
                  class="ma-0"
                  text
                  v-on="on"
                >
                  <v-icon :left="$vuetify.breakpoint.smAndUp">
                    {{ $t('Delivery.Button.Shift.Open.Icon') }}
                  </v-icon>&nbsp;

                  <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Delivery.Button.Shift.Open.Title') }}</span>
                </v-btn>
              </template>

              <v-card>
                <v-card-text>
                  <v-text-field
                    v-model="selectedDeliveryStaff.ShiftStartMoney"
                    :append-icon="appCurrency.Icon"
                    :hint="$t('Delivery.Button.Shift.Open.PopUp.Hint')"
                    :label="$t('Delivery.Button.Shift.Open.PopUp.Title')"
                    autofocus
                    name="input-order-comments"
                    persistent-hint
                  />
                </v-card-text>

                <v-divider />

                <v-card-actions>
                  <v-spacer />

                  <v-btn
                    class="primary--text"
                    text
                    @click="startDeliveryStaffShift()"
                  >
                    {{ $t('Delivery.Button.Shift.Open.PopUp.Button') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>

            <v-btn
              v-else-if="selectedDeliveryStaff.HasActiveShift && userCanManage"
              :color="$t('Delivery.Button.Shift.Close.Color')"
              :icon="$vuetify.breakpoint.xsOnly"
              class="ma-0"
              text
              @click="showCloseDeliveryStaffShiftDialog()"
            >
              <v-icon :left="$vuetify.breakpoint.smAndUp">
                {{ $t('Delivery.Button.Shift.Close.Icon') }}
              </v-icon>&nbsp;
              <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Delivery.Button.Shift.Close.Title') }}</span>
            </v-btn>
          </v-toolbar>

          <v-divider class="datatable-object" />

          <v-tabs
            v-model="activeTab"
            background-color="indigo"
            class="mt-1 datatable-object"
            dark
            grow
            slider-color="light-green"
          >
            <v-tab
              active-class="indigo darken-2 v-tabs__item--active"
              href="#tab1"
              ripple
            >
              {{ $t('Delivery.DeliveryStaffInfo.Tab.Details') }}
            </v-tab>

            <v-tab
              active-class="indigo darken-2 v-tabs__item--active"
              href="#tab2"
              ripple
            >
              {{ $t('Delivery.DeliveryStaffInfo.Tab.History') }}
            </v-tab>
          </v-tabs>

          <v-card-text class="pa-0 pt-2">
            <v-tabs-items v-model="activeTab">
              <!--ACTIVE SHIFT TAB-->
              <v-tab-item value="tab1">
                <v-card>
                  <v-card-title class="pa-0">
                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row dense>
                        <v-col
                          class="text-left"
                          cols="5"
                          sm="3"
                        >
                          <div
                            v-if="selectedDeliveryStaff.HasActiveShift"
                            class="text-center"
                          >
                            <div class="text-caption pl-1 pt-0 blue--text">
                              <strong>{{ $t('Delivery.Common.ShiftStart') }}</strong>
                            </div>

                            <div class="text-h6 grey--text text--darken-1">
                              {{ selectedDeliveryStaff.ShiftStartMoney | currency }}
                            </div>

                            <div
                              class="text-caption pl-1 pt-1 grey--text"
                              style="white-space: nowrap;"
                            >
                              {{ selectedDeliveryStaff.ShiftStartDateTime.split(' ')[0] }}
                            </div>

                            <div class="text-caption">
                              {{ selectedDeliveryStaff.ShiftStartDateTime.split(' ')[1] }}
                            </div>
                          </div>
                        </v-col>

                        <v-col
                          class="text-left"
                          cols="5"
                          md="3"
                        >
                          <div
                            v-if="selectedDeliveryStaff.HasActiveShift"
                            class="text-center"
                          >
                            <div class="text-caption pl-1 pt-0 orange--text">
                              <strong>{{ $t('Waiter.Common.ShiftTotal') }}</strong>
                            </div>

                            <div class="text-h6 grey--text text--darken-1">
                              {{ getDeliveryStaffShiftTotals(selectedDeliveryStaff).TotalExcluded | currency }}
                            </div>

                            <div
                              v-if="getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Cash.Active"
                              class="text-caption pl-1 pt-1"
                              style="white-space: nowrap;"
                            >
                              <span class="grey--text font-weight-bold">
                                {{ $t('Waiter.Common.Cash') }}
                              </span>

                              {{ getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Cash.Total | currency }}
                            </div>

                            <div
                              v-if="getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Card.Active"
                              class="text-caption pl-1 pt-1"
                              style="white-space: nowrap;"
                            >
                              <span class="grey--text font-weight-bold">
                                {{ $t('Waiter.Common.Card') }}
                              </span>

                              {{ getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Card.Total | currency }}
                            </div>

                            <div
                              v-if="getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Prepaid.Active"
                              class="text-caption pl-1 pt-1"
                              style="white-space: nowrap;"
                            >
                              <span class="grey--text font-weight-bold">
                                {{ $t('Waiter.Common.Prepaid') }}
                              </span>

                              {{ getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Prepaid.Total | currency }}
                            </div>

                            <div
                              v-if="getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Other.Active"
                              class="text-caption pl-1 pt-1"
                              style="white-space: nowrap;"
                            >
                              <span class="grey--text font-weight-bold">
                                {{ $t('Waiter.Common.Other') }}
                              </span>

                              {{ getDeliveryStaffShiftTotals(selectedDeliveryStaff, false).Other.Total | currency }}
                            </div>
                          </div>
                        </v-col>

                        <v-col
                          class="text-right flex-grow-1"
                          cols="auto"
                        >
                          <barcode
                            :id="getBarcode(selectedDeliveryStaff).barcode"
                            :style="$vuetify.breakpoint.xsOnly ? 'zoom: 0.85' : ''"
                            :text="(String(selectedDeliveryStaff.Firstname + ' ' + selectedDeliveryStaff.Lastname).toLocaleUpperCase($i18n.locale))"
                            :value="getBarcode(selectedDeliveryStaff).barcode"
                            class="print-item hidden-sm-and-down"
                            element-tag="svg"
                            font-size="12"
                            format="CODE128"
                            height="45"
                            text-margin="2"
                            text-position="bottom"
                            width="2"
                          />

                          <v-menu
                            v-if="appMachinePrintersActive.length > 1 && appHasPrinters && userCanManage"
                            left
                            nudge-bottom="30"
                            open-on-hover
                          >
                            <template #activator="{on}">
                              <v-btn
                                class="grey"
                                dark
                                icon
                                v-on="on"
                              >
                                <v-icon>print</v-icon>
                              </v-btn>
                            </template>

                            <v-list>
                              <template v-for="(printer, index) in appMachinePrintersActive">
                                <v-list-item
                                  :key="index"
                                  @click="onPrintDeliveryShift(printer)"
                                >
                                  <v-list-item-action>
                                    <v-icon>print</v-icon>
                                  </v-list-item-action>
                                  <v-list-item-title>{{ printer.name }}</v-list-item-title>
                                </v-list-item>
                              </template>
                            </v-list>
                          </v-menu>

                          <v-btn
                            v-if="(appMachinePrintersActive.length === 1 && appHasPrinters) && userCanManage && posUser?.Settings?.AllUsers?.Permissions?.CanPrintDeliveryShift"
                            class="grey"
                            dark
                            icon
                            @click.native="onPrintDeliveryShift(appMachinePrintersActive[0])"
                          >
                            <v-icon>print</v-icon>
                          </v-btn>

                          <div style="display: none;">
                            <div
                              class="barcode-generator-print-element"
                              style="text-align: center;"
                            >
                              <barcode
                                :id="getBarcode(selectedDeliveryStaff).barcode + '-PRINT'"
                                ref="barcodeCanvas"
                                :margin="0"
                                :value="getBarcode(selectedDeliveryStaff).barcode"
                                class="print-item"
                                element-tag="canvas"
                                font-size="12"
                                format="CODE128"
                                height="55"
                                text=" "
                                text-margin="2"
                                text-position="bottom"
                                width="2"
                              />
                            </div>
                          </div>
                        </v-col>

                        <v-col cols="12">
                          <v-divider />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row no-gutters>
                        <v-col cols="12">
                          <template v-if="selectedDeliveryStaff.HasActiveShift">
                            <v-alert
                              v-if="displayOrdersWarning"
                              :icon="false"
                              :style="$vuetify.theme.dark ? 'background-color: #252525' : 'background-color: #f6f6f6'"
                              border="left"
                              class="my-0 px-4 py-1"
                              colored-border
                              type="warning"
                              width="100%"
                            >
                              <v-chip
                                v-if="deliveryStaffShiftHasPendingPaymentCardOrders(selectedDeliveryStaff)"
                                :outlined="filterOrderNoCardPayment !== 2"
                                class="pl-2 pr-3 mr-3 my-1"
                                color="blue-grey"
                                dark
                                label
                                style="cursor: pointer; height: 38px;"
                                @click="onFilterWithPendingCardPayment"
                              >
                                <span class="">
                                  <v-badge
                                    color="blue-grey darken-2"
                                    inline
                                  >
                                    <template #badge>
                                      <span class="font-weight-bold">{{ deliveryStaffShiftPendingPaymentCardOrdersCount(selectedDeliveryStaff) }}</span>
                                    </template>
                                  </v-badge>

                                  {{ $tc('Delivery.Button.Shift.Close.PopUp.PendingCardPayments', deliveryStaffShiftPendingPaymentCardOrdersCount(selectedDeliveryStaff)) }}
                                </span>
                              </v-chip>

                              <v-chip
                                v-if="deliveryStaffShiftOrdersWithoutFiscalDocs(selectedDeliveryStaff)"
                                :outlined="filterOrderReceipt !== 2"
                                class="pl-2 pr-3 mr-3 my-1"
                                color="purple lighten-2"
                                dark
                                label
                                style="cursor: pointer; height: 38px;"
                                @click="onFilterWithoutFiscalDoc"
                              >
                                <span class="">
                                  <v-badge
                                    color="purple darken-2"
                                    inline
                                  >
                                    <template #badge>
                                      {{ deliveryStaffShiftOrdersWithoutFiscalDocsCount(selectedDeliveryStaff) }}
                                    </template>
                                  </v-badge>

                                  {{ $tc('Delivery.Button.Shift.Close.PopUp.WithoutFiscalDoc', deliveryStaffShiftOrdersWithoutFiscalDocsCount(selectedDeliveryStaff)) }}
                                </span>
                              </v-chip>
                            </v-alert>

                            <v-subheader
                              class="grey--text text--darken-2 px-2 px-md-4"
                              style="height: 50px"
                            >
                              <v-btn
                                v-if="userCanManage && posUserHasComponentPermission('Orders', 'MANAGE')"
                                :color="$t('Delivery.DeliveryStaffInfo.Button.AddOrder.Color')"
                                class="elevation-0 ml-0"
                                dark
                                small
                                @click.native="showAddOrderToDeliveryStaff"
                              >
                                {{ $t('Delivery.DeliveryStaffInfo.Button.AddOrder.Title') }}
                              </v-btn>

                              <filter-search
                                v-model="searchTerms"
                                :background-color="$vuetify.theme.dark ? 'grey darken-3' : ''"
                                class="ma-2"
                                max-width="160px"
                                padding="0 3px"
                                size="28px"
                              />

                              <v-spacer />

                              <span class="text-caption text-right">{{
                                selectedDeliveryStaffShiftOrders.length > 0 ? $t('Delivery.Common.ShiftsOrders') + selectedDeliveryStaffShiftOrders.length : $t('Delivery.DeliveryStaffInfo.Result.NoOrders')
                              }}</span>
                            </v-subheader>

                            <div
                              ref="ordersList"
                              class="mb-2"
                              style="overflow-y: auto;"
                            >
                              <orders-v-list
                                :orders="selectedDeliveryStaffShiftOrders"
                                :remove-action="posUserHasComponentPermission('Orders', 'MANAGE')"
                                @remove-action-click="showDeleteOrderDialog"
                                @list-tile-click="getOrderDetails"
                              />
                            </div>
                          </template>

                          <template v-else>
                            <div
                              ref="ordersList"
                              class="pr-2 mt-6"
                              style="overflow-y: auto;"
                            >
                              <div
                                class="fill-height"
                                style="position: relative;"
                              >
                                <div
                                  class="text-center grey--text pa-2"
                                  style="position: absolute; width: 100%; top: 50%; transform: translateY(-50%);"
                                >
                                  <v-icon
                                    class="mb-4"
                                    color="grey lighten-2"
                                    size="128"
                                  >
                                    {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.Icon') }}
                                  </v-icon>
                                  <div
                                    class="text-h6 mb-2"
                                    style="height: auto;"
                                  >
                                    {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.Title') }}
                                  </div>
                                  <div
                                    class="text-subtitle-1"
                                    style="height: auto;"
                                  >
                                    <a @click="startShiftPopOverVisible=true">{{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.SubtitleLink') }}</a>
                                    {{ $t('Delivery.DeliveryStaffInfo.Result.InactiveShift.SubtitleText') }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!--HISTORY SHIFTS TAB-->
              <v-tab-item value="tab2">
                <!--HISTORY SHIFTS TABLE-->
                <v-card
                  v-if="viewData"
                  flat
                >
                  <v-data-table
                    :footer-props="dataTable.footerProps"
                    :headers="dataTable.headers"
                    :height="dataTable.resize.bodyHeight"
                    :items="dataTable.data"
                    :loading="dataTable.loading"
                    :loading-text="dataTable.text.loadingText"
                    :no-data-text="dataTable.text.noDataText"
                    :no-results-text="dataTable.text.noResultsText"
                    :options.sync="dataTable.options"
                    :server-items-length="dataTable.total"
                    fixed-header
                    item-key="Id"
                  >
                    <template #[`item.date_started`]="{item}">
                      {{ item.ShiftsDate }}
                    </template>

                    <template #[`item.swifts`]="{item}">
                      {{ item.ShiftsCount }}
                    </template>

                    <template #[`item.orders`]="{item}">
                      {{ item.OrdersCount }}
                    </template>

                    <template #[`item.total`]="{item}">
                      {{ item.OrdersTotal | currency }}
                    </template>

                    <template #[`item.actions`]="{item}">
                      <v-tooltip
                        :color="$t('Delivery.DeliveryStaffInfo.Button.ViewDailyShift.Color')"
                        left
                        open-delay="1000"
                      >
                        <template #activator="{on}">
                          <v-btn
                            :class="$t('Delivery.DeliveryStaffInfo.Button.ViewDailyShift.Color')"
                            :small="$vuetify.breakpoint.xsOnly"
                            class="elevation-2 ml-1"
                            dark
                            icon
                            v-on="on"
                            @click.native.stop="showDeliveryShifts(item)"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              {{ $t('Delivery.DeliveryStaffInfo.Button.ViewDailyShift.Icon') }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('Delivery.DeliveryStaffInfo.Button.ViewDailyShift.Title') }}</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="!selectedDeliveryStaff">
        <v-container
          class="pa-0 fill-height"
          fluid
        >
          <v-row class="fill-height align-center justify-center">
            <v-col
              class="text-center grey--text"
              cols="12"
            >
              <v-icon
                class="mb-4"
                color="grey lighten-2"
                size="128"
              >
                {{ $t('Delivery.ListView.SelectDeliveryStaff.Icon') }}
              </v-icon>
              <div
                class="text-h6 mb-2"
                style="height: auto;"
              >
                {{ $t('Delivery.ListView.SelectDeliveryStaff.Title') }}
              </div>
              <div
                class="text-subtitle-1"
                style="height: auto;"
              >
                {{ $t('Delivery.ListView.SelectDeliveryStaff.Subtitle') }}
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <confirm-dialog
      :html-content="$t('Delivery.Button.Shift.Close.PopUp.Content')"
      :html-title="$t('Delivery.Button.Shift.Close.Title')"
      :info-bar="displayOrdersWarning"
      :info-bar-body="getDeliveryStaffShiftOrdersWarningText()"
      :visible.sync="confirmDialogOpenShiftVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="close-shift-on-yes-btn"
      @close-shift-on-yes-btn="closeDeliveryStaffShift"
    />

    <confirm-dialog
      :html-content="$t('Delivery.Button.DeliveryStaff.Delete.PopUp.Content')"
      :html-title="$t('Delivery.Button.DeliveryStaff.Delete.PopUp.Title')"
      :visible.sync="confirmDialogDeleteDeliveryStaffVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="delete-delivery-staff-on-yes-btn"
      @delete-delivery-staff-on-yes-btn="deleteDeliveryStaff"
    />

    <confirm-dialog
      :html-content="$t('Delivery.Button.RemoveOrder.PopUp.Content')"
      :html-title="$t('Delivery.Button.RemoveOrder.PopUp.Title')"
      :visible.sync="confirmDialogDeleteOrderVisible"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="delete-order-on-yes-btn"
      @delete-order-on-yes-btn="deleteOrder"
    />

    <delivery-staff-add-dialog
      :delivery-staff-to-edit="tmpDeliveryStaff"
      :has-eft-pos-delivery-staff="hasEftPosDeliveryStaff"
      :visible.sync="addEditDeliveryStaffDialog"
    />

    <delivery-staff-day-shifts-dialog
      :selected-delivery-staff="selectedDeliveryStaff"
      :selected-shifts-day="selectedShiftsDay"
      :tmp-order.sync="tmpOrder"
      :visible.sync="selectedShiftsDayDialogVisible"
    />

    <select-order-dialog
      :selected-order.sync="selectedOrder"
      :visible.sync="selectedOrderDialogVisible"
    />
  </v-container>
</template>

<script>
import moment from 'moment'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import Resizable from '@/mixins/resizable'
import BarcodeGenerator from '@/lib/barcode/BarcodeGenerator'
import SelectOrderDialog from '@/components/orders/SelectOrderDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import OrdersVList from '@/components/orders/common/OrdersVList'
import DeliveryStaffAddDialog from '@/components/delivery/DeliveryStaffAddDialog'
import DeliveryStaffDayShiftsDialog from '@/components/delivery/DeliveryStaffDayShiftsDialog'
import BarcodePrefixEnum from '@/api/Enums/BarcodePrefixEnum'
import DataTable from '@/mixins/data-table/dataTable'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import {clone} from '@/lib/helper/helper.js'
import DeliveryStaffCommon from '@/mixins/delivery/deliveryStaffCommon.js'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import greekUtils from '@/lib/greek-utils/index.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum.js'

export default {
  components: {
    FilterSearch,
    'orders-v-list'                   : OrdersVList,
    'delivery-staff-add-dialog'       : DeliveryStaffAddDialog,
    'delivery-staff-day-shifts-dialog': DeliveryStaffDayShiftsDialog,
    'confirm-dialog'                  : ConfirmDialog,
    'barcode'                         : BarcodeGenerator,
    'select-order-dialog'             : SelectOrderDialog
  },
  directives: {},
  mixins    : [AppData, Auth, Resizable, DataTable, DeliveryStaffCommon],
  props     : [],

  data() {
    return {
      activeTab  : 'tab1',
      searchTerms: '',
      dataTable  : {
        dataStore: null,
        options  : {
          defaultSortBy: 'date_started',
          sortBy       : ['date_started'],
          sortDesc     : [true]
        },
        resize: {
          offset: 20
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Delivery.ListView.Table.Headers.DateStarted'),
            value    : 'date_started',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Delivery.ListView.Table.Headers.Swifts'),
            value    : 'swifts',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Delivery.ListView.Table.Headers.Orders'),
            value    : 'orders',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Delivery.ListView.Table.Headers.Total'),
            value    : 'total',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Delivery.ListView.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2'
          }
        ]
      },
      selectedDeliveryStaff                  : null,
      tmpDeliveryStaff                       : null,
      tmpOrder                               : null,
      selectedOrder                          : null,
      selectedShiftsDay                      : null,
      selectedShiftsDayDialogVisible         : null,
      startShiftPopOverVisible               : false,
      confirmDialogOpenShiftVisible          : false,
      confirmDialogDeleteDeliveryStaffVisible: false,
      confirmDialogDeleteOrderVisible        : false,
      addEditDeliveryStaffDialog             : false,
      selectedOrderDialogVisible             : false,
      filterOrderNoCardPayment               : 0,
      filterOrderReceipt                     : 0
    }
  },

  computed: {
    displayOrdersWarning() {
      return this.userIsSuperAdmin
          && this.appHasFiscalDocsProvider
          && this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD
          && (this.deliveryStaffShiftHasPendingPaymentCardOrders(this.selectedDeliveryStaff) || this.deliveryStaffShiftOrdersWithoutFiscalDocs(this.selectedDeliveryStaff))
    },

    deliveryStaff() {
      const items = clone(window.Vue.deliveryStaffItems || [])
      const eftPosItemIndex = items.findIndex(item => item?.User?.EftPos)
      if (eftPosItemIndex > -1) items.push(items.splice(eftPosItemIndex, 1)[0])
      return items
    },

    selectedDeliveryStaffShiftOrders() {
      const orders = this.selectedDeliveryStaff?.ShiftOrders || []
      const searchString = greekUtils.toGreeklish(String(this.searchTerms).toLocaleLowerCase(this.$i18n.locale))
      return orders
        .filter(o => this.filterOrderReceipt ? !o.HasFiscalDocument : true)
        .filter(o => this.filterOrderNoCardPayment ? this.orderIsWithUnpaidCardTransactions(o) && o?.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType))) : true)
        .filter(o => {
          const itemString = [
            o.Id && String(o.Id),
            o.OrderCode && String(o.OrderCode),
            o.OrderCode && String(o.OrderCode).replace('-', ''),
            o.Email && String(o.Email),
            o.Address && greekUtils.toGreeklish(JSON.stringify(o.Address).toLocaleLowerCase(this.$i18n.locale)),
            o.FirstName && greekUtils.toGreeklish(String(o.FirstName).toLocaleLowerCase(this.$i18n.locale)),
            o.LastName && greekUtils.toGreeklish(String(o.LastName).toLocaleLowerCase(this.$i18n.locale)),
            o.StaffFirstname && greekUtils.toGreeklish(String(o.StaffFirstname).toLocaleLowerCase(this.$i18n.locale)),
            o.StaffLastname && greekUtils.toGreeklish(String(o.StaffLastname).toLocaleLowerCase(this.$i18n.locale)),
            o.Table?.TableAreaName && greekUtils.toGreeklish(String(o.Table.TableAreaName).toLocaleLowerCase(this.$i18n.locale)),
            o.Table?.TableName && greekUtils.toGreeklish(String(o.Table.TableName).toLocaleLowerCase(this.$i18n.locale)),
            o.Telephone && String(o.Telephone),
            o.Telephone2 && String(o.Telephone2),
            o.Telephone3 && String(o.Telephone3)
          ].filter(Boolean).join(' ')

          return itemString.includes(searchString)
        })
    },

    hasEftPosDeliveryStaff() {
      return this.deliveryStaff.some(item => item?.User?.EftPos)
    }
  },

  watch: {
    selectedOrder(newVal) {
      if (newVal) {
        window.callAS(window.SocketCommand.Staff.Delivery.Shift.AddOrder, {
          Id     : this.selectedDeliveryStaff.Id,
          OrderId: newVal.Id
        })
        this.selectedOrder = null
      }
    },

    activeTab(newVal) {
      if (newVal === 'tab2') {
        this.getData()
      }
    }
  },

  created() {
  },

  mounted() {
    this.$bus.$on(window.SocketCommand.Staff.Delivery.All, this.onDeliveryStaffList)
    this.$bus.$on(window.SocketCommand.Order.GetDetails, this.onOrderDetailsResponse)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.Shift.AddOrder, this.getAllData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.Shift.RemoveOrder, this.getAllData)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.ShiftsHistory, this.setData)

    this.getAllData()
  },

  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Staff.Delivery.All, this.onDeliveryStaffList)
    this.$bus.$off(window.SocketCommand.Order.GetDetails, this.onOrderDetailsResponse)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.Shift.AddOrder, this.getAllData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.Shift.RemoveOrder, this.getAllData)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.ShiftsHistory, this.setData)
  },
  methods: {
    onFilterWithoutFiscalDoc() {
      this.filterOrderReceipt = this.filterOrderReceipt === 2 ? this.filterOrderReceipt = 0 : this.filterOrderReceipt = 2
    },

    onFilterWithPendingCardPayment() {
      this.filterOrderNoCardPayment = this.filterOrderNoCardPayment === 2 ? this.filterOrderNoCardPayment = 0 : this.filterOrderNoCardPayment = 2
    },

    orderIsWithUnpaidCardTransactions(o) {
      return o?.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType)) && trx?.Status !== 'APPROVED')
    },

    showDeliveryShifts(shiftsDay) {
      this.selectedShiftsDay = shiftsDay
      this.selectedShiftsDayDialogVisible = true
    },

    showAddOrderToDeliveryStaff() {
      this.selectedOrderDialogVisible = true
    },

    showDeliveryStaffAddDialog() {
      this.tmpDeliveryStaff = null
      this.addEditDeliveryStaffDialog = true
    },

    showDeliveryStaffEditDialog(staff) {
      this.tmpDeliveryStaff = staff
      this.addEditDeliveryStaffDialog = true
    },

    showCloseDeliveryStaffShiftDialog() {
      this.confirmDialogOpenShiftVisible = true
    },

    deliveryStaffShiftOrdersWithoutFiscalDocsCount(staff) {
      if (!staff || (!this.appHasFiscalDocsProvider && this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD)) return false

      const shiftOrders = staff?.ShiftOrders || []
      return shiftOrders.filter(order => !order?.HasFiscalDocument).length
    },
    deliveryStaffShiftOrdersWithoutFiscalDocs(staff) {
      if (!staff || (!this.appHasFiscalDocsProvider && this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD)) return false

      return this.deliveryStaffShiftOrdersWithoutFiscalDocsCount(staff) > 0
    },

    getDeliveryStaffShiftOrdersWarningText() {
      const pendingCardPayment = this.$tc('Delivery.Button.Shift.Close.PopUp.PendingCardPayments', this.deliveryStaffShiftPendingPaymentCardOrdersCount(this.selectedDeliveryStaff), {ordersCount: this.deliveryStaffShiftPendingPaymentCardOrdersCount(this.selectedDeliveryStaff)})
      const withoutFiscalDocs = this.$tc('Delivery.Button.Shift.Close.PopUp.WithoutFiscalDoc', this.deliveryStaffShiftOrdersWithoutFiscalDocsCount(this.selectedDeliveryStaff), {ordersCount: this.deliveryStaffShiftOrdersWithoutFiscalDocsCount(this.selectedDeliveryStaff)})

      if (this.deliveryStaffShiftHasPendingPaymentCardOrders(this.selectedDeliveryStaff) && this.deliveryStaffShiftOrdersWithoutFiscalDocs(this.selectedDeliveryStaff)) return pendingCardPayment + ' ' + withoutFiscalDocs
      if (this.deliveryStaffShiftHasPendingPaymentCardOrders(this.selectedDeliveryStaff)) return pendingCardPayment
      if (this.deliveryStaffShiftOrdersWithoutFiscalDocs(this.selectedDeliveryStaff)) return withoutFiscalDocs

      return ''
    },

    deliveryStaffShiftPendingPaymentCardOrdersCount(staff) {
      if (!staff || (!this.appHasFiscalDocsProvider && this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD)) return false

      const shiftOrders = staff?.ShiftOrders || []
      return shiftOrders.filter(order => !order?.ExternalOrderId && order?.PaymentTransactions?.some(trx => (PaymentTypeEnum.isDeliveryCard(trx.PaymentType) || PaymentTypeEnum.isEftPosPaymentType(trx.PaymentType)) && trx?.Status !== 'APPROVED')).length
    },
    deliveryStaffShiftHasPendingPaymentCardOrders(staff) {
      if (!staff || (!this.appHasFiscalDocsProvider && this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD)) return false

      return this.deliveryStaffShiftPendingPaymentCardOrdersCount(staff) > 0
    },

    showDeleteDeliveryStaffDialog(staff) {
      this.tmpDeliveryStaff = staff
      this.confirmDialogDeleteDeliveryStaffVisible = true
    },

    showDeleteOrderDialog(order) {
      this.tmpOrder = order
      this.confirmDialogDeleteOrderVisible = true
    },

    getDateTimeNow() {
      return moment().format('DD/MM/YYYY HH:mm')
    },

    deliveryStaffToggle(staff) {
      this.searchTerms = ''
      this.filterOrderNoCardPayment = 0
      this.filterOrderReceipt = 0

      if (staff?.User?.EftPos) {
        this.selectedDeliveryStaff = null
        this.showDeliveryStaffEditDialog(staff)
        return
      }

      this.activeTab = 'tab1'
      this.dataTable.data = []

      if (this.selectedDeliveryStaff === staff) {
        this.selectedDeliveryStaff = null
      } else {
        this.selectedDeliveryStaff = staff
      }

      this.$nextTick(function () {
        this.onResize()
      })
    },

    startDeliveryStaffShift() {
      this.startShiftPopOverVisible = false

      if (this.selectedDeliveryStaff) {
        window.callAS(window.SocketCommand.Staff.Delivery.Shift.Open, {
          Id   : this.selectedDeliveryStaff.Id,
          Money: this.selectedDeliveryStaff?.ShiftStartMoney || 0
        }, window.SocketCommand.Staff.Delivery.All)
      }
    },

    closeDeliveryStaffShift() {
      if (this.selectedDeliveryStaff) {
        window.callAS(window.SocketCommand.Staff.Delivery.Shift.Close, {Id: this.selectedDeliveryStaff.Id}, window.SocketCommand.Staff.Delivery.All)
      }
    },

    deleteDeliveryStaff() {
      if (this.tmpDeliveryStaff) {
        window.callAS(window.SocketCommand.Staff.Delivery.Delete, {Id: this.tmpDeliveryStaff.Id}, window.SocketCommand.Staff.Delivery.All)
      }
      this.tmpDeliveryStaff = null
    },

    deleteOrder() {
      if (this.selectedDeliveryStaff && this.tmpOrder) {
        window.callAS(window.SocketCommand.Staff.Delivery.Shift.RemoveOrder, {
          DeliveryStaffShiftOrderId: this.tmpOrder.DeliveryStaffShiftOrderId,
          DeliveryStaffShiftId     : this.selectedDeliveryStaff.DeliveryStaffShiftId,
          OrderId                  : this.tmpOrder.Id
        })
      }
      this.tmpOrder = null
    },

    getAllData() {
      window.callAS(window.SocketCommand.Staff.Delivery.All, {})
    },

    getOrderDetails(orderItem) {
      this.tmpOrder = orderItem
      window.callAS(window.SocketCommand.Order.GetDetails, {
        order_id : orderItem.Id,
        status_id: orderItem.StatusId
      })
    },

    getBarcode(staff) {
      const barcode = {
        barcode: BarcodePrefixEnum.DeliveryStaff + staff.Id,
        value  : staff.Id
      }

      return barcode
    },

    getOrderListItemColor(order) {
      let colorClass = ''
      colorClass = order.Payment === PaymentTypeEnum.CASH ? 'green lighten-5' : 'blue-grey lighten-5'
      colorClass = parseInt(order.StatusId) !== 19 ? colorClass : 'red lighten-5'
      return colorClass
    },

    onOrderDetailsResponse(data) {
      if (this.tmpOrder) {
        this.tmpOrder.Details = data
        this.$bus.$emit('show-order-details-dialog', this.tmpOrder)
      }
    },

    onDeliveryStaffList(data) {
      if (data && data.status === 'success') {
        if (this.selectedDeliveryStaff) {
          this.$nextTick(() => {
            this.selectedDeliveryStaff = this.deliveryStaff.find(staff => staff.Id.toString() === this.selectedDeliveryStaff.Id.toString())
          })
        }

        this.$nextTick(() => {
          this.onResize()
        })

        if (data.message) {
          this.$bus.$emit('app-show-notification', {
            body: data.message,
            type: 'success',
            icon: 'info'
          })
        }
      } else if (data && data.status === 'fail' && data.message) {
        this.$bus.$emit('app-show-notification', {
          body: data.message,
          type: 'error',
          icon: 'warning'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.GenericError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    getData() {
      if (this.selectedDeliveryStaff) {
        this.dataTable.loading = true
        window.callAS(window.SocketCommand.Staff.Delivery.ShiftsHistory, {
          id    : this.selectedDeliveryStaff.Id,
          ...this.dataTableRequestData(),
          config: 0
        })
      }
    },
    setData(data) {
      this.setDataInternal(data)
    },

    onResize() {
      const deliveryStaffList = this.$refs.deliveryStaffList
      const ordersList = this.$refs.ordersList

      const headerHeight = document.getElementById('appToolbar')?.offsetHeight ? document.getElementById('appToolbar').offsetHeight : 0
      const footerHeight = document.getElementById('appFooter')?.offsetHeight ? document.getElementById('appFooter').offsetHeight : 0
      const deliveryStaffToolbarHeight = this.$refs.deliveryStaffToolbar ? this.$refs.deliveryStaffToolbar.$el.clientHeight : 0

      if (deliveryStaffList) deliveryStaffList.style.height = this.$vuetify.breakpoint.smAndDown ? '45px' : (innerHeight - headerHeight - footerHeight - deliveryStaffToolbarHeight - 20) + 'px'

      if (ordersList && this.$vuetify.breakpoint.mdAndUp) {
        ordersList.style.height = ordersList.style.minHeight = (innerHeight - headerHeight - footerHeight - deliveryStaffToolbarHeight - (this.selectedDeliveryStaff?.HasActiveShift ? 293 : 235) - (this.displayOrdersWarning ? 54 : 0)) + 'px'
      }
    }
  }
}
</script>
